import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import {
  signInWithGoogle as authSignIn,
  signOutUser,
  createUserDocument,
  getUserDocument,
} from '../services/auth';
import { UserDocument, AuthContextValue } from '../types/user';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          await createUserDocument(firebaseUser);
          const doc = await getUserDocument(firebaseUser.uid);
          setUserDoc(doc);
        } catch (err) {
          console.error('Failed to sync user document:', err);
          setError('Failed to load user profile. Please refresh.');
        }
      } else {
        setUserDoc(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async () => {
    setError(null);
    const result = await authSignIn();

    if (!result.success && !result.silent) {
      setError(result.error || "Couldn't sign in. Please try again.");
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    await signOutUser();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshUserDoc = useCallback(async () => {
    if (!user) return;
    try {
      const doc = await getUserDocument(user.uid);
      setUserDoc(doc);
    } catch (err) {
      console.error('Failed to refresh user document:', err);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userDoc,
        loading,
        error,
        signIn,
        signOut,
        clearError,
        refreshUserDoc,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
